import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  MessageRow, MessageAvatar, MessageContent, Bubble, MsgTime,
  TypingBubble, SystemPill
} from './ChatStyles';

const ServiceQuestionnaireBubble = ({ data, scheme, onClick }) => {
  const serviceColors = {
    real_estate: '#3b82f6', employment: '#fb923c',
    english_academy: '#8b5cf6', finance: '#10b981',
  };
  const color = serviceColors[data.serviceType] || '#fb923c';

  return (
    <div onClick={onClick} style={{
      background: `linear-gradient(135deg, ${color}15 0%, ${color}20 100%)`,
      padding: '16px 20px', borderRadius: 14, maxWidth: '85%', minWidth: 250,
      border: `1px solid ${color}30`, cursor: 'pointer', transition: 'all 0.2s',
      boxShadow: `0 4px 16px ${color}20`,
    }}>
      <div style={{ fontSize: 28, marginBottom: 8 }}>{data.serviceIcon || '📋'}</div>
      <div style={{ fontSize: 15, fontWeight: 600, color, marginBottom: 6 }}>
        {data.serviceName || 'Service Questionnaire'}
      </div>
      <div style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.5, marginBottom: 12 }}>
        {getServiceDescription(data.serviceType)}
      </div>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: `linear-gradient(135deg, ${color}, ${color}cc)`,
        color: '#fff', padding: '10px 16px', borderRadius: 8, fontWeight: 600, fontSize: 13,
      }}>
        <span>Start Questionnaire</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
};

const getServiceDescription = (serviceType) => {
  switch (serviceType) {
    case 'real_estate': return 'Answer a few questions to help us find your perfect home.';
    case 'employment': return 'Complete your professional profile to get matched with opportunities.';
    case 'english_academy': return 'Tell us about your language goals to find the right courses.';
    case 'finance': return 'Share your financial goals and we\'ll help you plan your next steps.';
    default: return 'Click to complete this questionnaire.';
  }
};

const isServiceQuestionnaireContent = (content) => {
  if (!content || typeof content !== 'string') return false;
  try {
    const parsed = JSON.parse(content);
    if (parsed && parsed.sessionId && parsed.serviceType && parsed.sessionUrl) return true;
    if (parsed && parsed.type === 'external_advisor_questionnaire' && parsed.serviceType) return true;
    return false;
  } catch { return false; }
};

/**
 * Remove the engine's control markers from an assistant reply before it is rendered.
 * The model does not always keep the brackets (a YK conversation on 2026-10-20 ended with a
 * visible "LANGUAGE:en"), so both forms go: LANGUAGE:[xx] / LANGUAGE:xx / LANGUAGE: xx-yy,
 * CONVERSATION:[DONE] / CONVERSATION:DONE, [DONE], [PROGRESS:n/m], [CLASSIFICATION:…].
 * The bare language code must not be followed by a letter, so "Language: English" survives.
 * widget-server strips these too; this is the display-side guarantee (old stored messages,
 * resumed conversations). Completion is signalled by finishFlag, not by this text.
 */
export const stripEngineMarkers = (html) => {
  if (typeof html !== 'string') return html;
  return html
    .replace(/(?:<strong>\s*)?\bLANGUAGE:\s*(?:<\/strong>\s*)?(?:\[\s*[a-z]{2}(?:-[a-z]{2,4})?\s*\]|[a-z]{2}(?:-[a-z]{2,4})?(?![a-z]))/gi, '')
    .replace(/\bCONVERSATION:\s*\[?\s*DONE\s*\]?/g, '')
    .replace(/\bCONVERSATION:(?=\s*(?:<|$|LANGUAGE\b))/gm, '') // dangling, its [DONE] stripped upstream
    .replace(/\[DONE\]/g, '')
    .replace(/\[PROGRESS:\s*\d+\s*\/\s*\d+\]/gi, '')
    .replace(/\[CLASSIFICATION:[^\]]*\]/gi, '')
    .replace(/<strong>\s*<\/strong>/gi, '')
    .replace(/<p>\s*<\/p>/gi, '')
    .trim();
};

const ChatMessage = ({ message: rawMessage, type, loading, timestamp, darkMode, scheme = 'blue' }) => {
  // Only assistant/system text carries engine markers; never rewrite what the visitor typed.
  const message = type === 'user' ? rawMessage : stripEngineMarkers(rawMessage);
  const cleanHTML = DOMPurify.sanitize(message, {
    HOOK: {
      afterSanitizeAttributes: function (node) {
        if (node.tagName === 'TH' && node.style) node.style.backgroundColor = '';
      }
    }
  });
  const [displayMessage, setDisplayMessage] = useState(cleanHTML);

  useEffect(() => {
    if (loading) {
      setDisplayMessage('....');
    } else {
      const cleanHTML = DOMPurify.sanitize(message, {
        HOOK: {
          afterSanitizeAttributes: function (node) {
            if (node.tagName === 'TH' && node.style) node.style.backgroundColor = '';
          }
        }
      });
      setDisplayMessage(cleanHTML);
    }
  }, [loading, message]);

  if (type?.startsWith('service_questionnaire_') || isServiceQuestionnaireContent(message)) {
    let questionnaireData;
    try {
      questionnaireData = typeof message === 'string' ? JSON.parse(message) : message;
    } catch {
      questionnaireData = { serviceName: 'Service Questionnaire', serviceIcon: '📋', serviceType: 'employment' };
    }

    return (
      <MessageRow $type="gpt">
        <MessageAvatar $type="gpt" $scheme={scheme}>✦</MessageAvatar>
        <MessageContent>
          <ServiceQuestionnaireBubble
            data={questionnaireData}
            scheme={scheme}
            onClick={() => questionnaireData.sessionUrl && window.open(questionnaireData.sessionUrl, '_blank', 'noopener,noreferrer')}
          />
          {timestamp && <MsgTime $type="gpt">{timestamp}</MsgTime>}
        </MessageContent>
      </MessageRow>
    );
  }

  if (type === 'system') {
    return (
      <SystemPill>
        <span dangerouslySetInnerHTML={{ __html: displayMessage }} />
      </SystemPill>
    );
  }

  return (
    <MessageRow $type={type}>
      <MessageAvatar $type={type} $scheme={scheme}>
        {type === 'user' ? (
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <circle cx="12" cy="8" r="4" /><path d="M4 20c0-3.3 3.6-6 8-6s8 2.7 8 6" />
          </svg>
        ) : '✦'}
      </MessageAvatar>
      <MessageContent>
        <Bubble $type={type} $scheme={scheme}>
          {loading ? (
            <TypingBubble style={{ background: 'transparent', border: 'none', padding: 0 }}>
              <span /><span /><span />
            </TypingBubble>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: displayMessage }} />
          )}
        </Bubble>
        {timestamp && !loading && <MsgTime $type={type}>{timestamp}</MsgTime>}
      </MessageContent>
    </MessageRow>
  );
};

export default ChatMessage;
